import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Input,
  FormGroup,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import "./customer.scss";
import { put, post, get } from "helpers/api_helper";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import * as api from "../../constants/api_constants";
import CustomPagination from "components/CustomPagination";
import SummaryCards from "components/SummaryCards";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { formatToPascal } from "helpers/utils/nameUtils";

function Customers() {
  const navigate = useNavigate();

  // State for filtered customers, selected status filter, and search query
  const [totalCustomers, setTotalCustomers] = useState({
    customers: [],
    error: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [customersCount, setCustomersCount] = useState([]);
  const [apiStatus, setApiStatus] = useState({ loading: false });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    sortBy: "customerId",
    direction: "DESC",
  });
  const timerRef = useRef(null);

  const fetchData = useCallback(
    async (query, page = 1) => {
      try {
        setApiStatus((prev) => ({ ...prev, loading: true }));
        const data = await post(api.GET_ALL_CUSTOMERS, {
          customerTO: {
            subscriptionId: 100,
            searchKeyword: query?.trim() ?? "",
          },
          pageSize: 10,
          page: page - 1,
          sortBy: sortConfig.sortBy,
          direction: sortConfig.direction,
        });

        const custCountData = await get(api.GET_CUSTOMERS_COUNT_DATA);
        if (custCountData) {
          setCustomersCount(custCountData);
          setTotalPages(data.customerPageResponse?.totalPages || 0);
        }

        if (data) {
          setTotalCustomers((prev) => ({
            ...prev,
            customers: data?.customerPageResponse?.data,
            error: "",
          }));
        }
      } catch ({ response: { data: { message } = {} } = {} }) {
        setCustomersCount(0);
        setTotalCustomers((prev) => ({
          ...prev,
          customers: [],
          error: message ?? "",
        }));
      }

      setApiStatus((prev) => ({ ...prev, loading: false }));
    },
    [sortConfig]
  );

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      fetchData(searchQuery, page);
    },
    [fetchData, searchQuery]
  );

  const handleSort = useCallback((columnName) => {
    setSortConfig((prevConfig) => {
      // If sorting the same column, toggle direction
      if (prevConfig.sortBy === columnName) {
        return {
          sortBy: columnName,
          direction: prevConfig.direction === "ASC" ? "DESC" : "ASC",
        };
      }
      // If sorting a new column, default to ASC
      return {
        sortBy: columnName,
        direction: "ASC",
      };
    });
  }, []);

  const SortArrows = ({ column, currentSort }) => {
    const isCurrentColumn = currentSort.sortBy === column;
    return (
      <span className="sort-arrows">
        <i
          className={`fas fa-sort-up ${
            isCurrentColumn && currentSort.direction === "ASC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
        <i
          className={`fas fa-sort-down ${
            isCurrentColumn && currentSort.direction === "DESC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
      </span>
    );
  };

  // Mapping of table columns to API sort keys
  const columnSortMap = {
    "Customer Code": "customerCode",
    Company: "companyName",
    "Primary Contact": "primaryContactName",
    "Primary Email": "email",
    Designation: "designation",
    Status: "status",
  };

  const renderTableHeader = () => (
    <thead>
      <tr>
        {[
          "Customer Code",
          "Company",
          "Primary Contact",
          "Primary Email",
          "Designation",
          "Status",
          "Actions",
        ].map((header) =>
          header !== "Actions" ? (
            <th
              key={header}
              onClick={() => handleSort(columnSortMap[header])}
              style={{ cursor: "pointer" }}
            >
              {header}
              <SortArrows
                column={columnSortMap[header]}
                currentSort={sortConfig}
              />
            </th>
          ) : (
            <th key={header}>{header}</th>
          )
        )}
      </tr>
    </thead>
  );

  const toggleCustomerStatus = useCallback(
    async (id) => {
      const customerData = totalCustomers.customers.find(
        (each) => each.customerId === id
      );
      if (customerData) {
        try {
          const updatedCustomer = {
            customerId: customerData.customerId,
            status: customerData.status === 1 ? 0 : 1,
          };
          await put(api.UPDATE_CUSTOMER_DATA_BY_ID(id), {
            customerTO: updatedCustomer,
          });
          showSuccessToast(`Customer status updated successfully`);
          await fetchData(searchQuery, currentPage);
        } catch (error) {
          showErrorToast(`Failed to update customer status`);
          console.log("Error fetching data", error);
        }
      }
    },
    [fetchData, searchQuery, currentPage, totalCustomers.customers]
  );

  const exportToCSV = () => {
    const headers = [
      "Customer Code",
      "Company",
      "Primary Contact",
      "Primary Email",
      "Designation",
      "Status",
    ];
    const rows = totalCustomers.customers.map((user) => [
      user.customerCode,
      user.companyName,
      user.primaryContactName,
      user.email,
      user.designation,
      user.status,
    ]);

    let csvContent =
      "data:text/csv;charset=utf-8," +
      headers.join(",") +
      "\n" +
      rows.map((row) => row.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "customers.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEdit = useCallback(
    (id) => {
      navigate(`/add-customer/${id}`);
    },
    [navigate]
  );
  const handleView = useCallback(
    (id) => {
      navigate(`/view-customer/${id}`);
    },
    [navigate]
  );

  const handleSearch = useCallback(
    (e) => {
      setSearchQuery(e.target.value ?? "");

      const query = e.target.value || "";

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        fetchData(query?.trim());
      }, 300);
    },
    [fetchData]
  );

  useEffect(() => {
    fetchData(searchQuery, currentPage);
  }, [fetchData, sortConfig, searchQuery, currentPage]);

  const summaryData = [
    {
      iconClass: "fas fa-users text-primary",
      label: "Total Customers",
      value: customersCount.totalCount,
      countClass: "text-blue",
    },
    {
      iconClass: "fas fa-user-check text-primary",
      label: "Active Customers",
      value: customersCount.activeCustomers,
      countClass: "text-primary",
    },
    {
      iconClass: "fas fa-user-times text-danger",
      label: "Inactive Customers",
      value: customersCount.inactiveCustomers,
      countClass: "text-primary",
    },
  ];

  return (
    <div className="page-content">
      <Container fluid={true} style={{ padding: "0px", gap: "10px" }}>
        <PageSubHeader
          heading={"Customers Summary"}
          routePath={"Home"}
          pageName={"Customers"}
        />
        <Row className="px-4">
          <SummaryCards data={summaryData} />

          {/* Search and Export Row and add customer */}
          <Row className="align-items-center row-gap-3 justify-content-between py-2">
            <Col md={4}>
              <InputGroup size="sm" className="elbert-search-group">
                <Input
                  type="text"
                  placeholder="Search customers"
                  onChange={handleSearch}
                  value={searchQuery}
                  className="elbert-search"
                />
                <InputGroupText>
                  <span className="fa fa-search"></span>
                </InputGroupText>
              </InputGroup>
            </Col>
            <Col className="d-flex gap-3 align-items-center justify-content-end">
              <Button
                color="primary"
                outline
                className="waves-effect waves-light"
                onClick={exportToCSV}
              >
                Export CSV
              </Button>
              <CustomButton
                text={"+ Add New"}
                onClick={() => navigate("/add-customer")}
              />
            </Col>
          </Row>

          <Row className="py-2">
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="table-responsive">
                    <Table className="table mb-0">
                      <colgroup>
                        <col style={{ width: "12%" }} /> {/* Customer Code */}
                        <col style={{ width: "20%" }} /> {/* Company */}
                        <col style={{ width: "18%" }} /> {/* Primary Contact */}
                        <col style={{ width: "20%" }} /> {/* Primary Email */}
                        <col style={{ width: "12%" }} /> {/* Designation */}
                        <col style={{ width: "8%" }} /> {/* Status */}
                        <col style={{ width: "10%" }} /> {/* Actions */}
                      </colgroup>
                      {renderTableHeader()}
                      <tbody>
                        {totalCustomers?.customers?.map((user, index) => (
                          <tr key={index}>
                            <td>{user?.customerCode}</td>
                            <td>{user.companyName}</td>
                            <td>{user.primaryContactName}</td>
                            <td>{user.email}</td>
                            <td>{formatToPascal(user.designation)}</td>
                            <td>
                              <div>
                                <FormGroup switch>
                                  <Input
                                    style={{
                                      color: "blue",
                                      height: "1.4em",
                                      width: "3em",
                                      cursor: "pointer",
                                    }}
                                    type="switch"
                                    disabled={apiStatus.loading}
                                    id={`customerStatus-${user.customerId}`}
                                    checked={!!user.status}
                                    onChange={() =>
                                      toggleCustomerStatus(user.customerId)
                                    }
                                  />
                                </FormGroup>
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="d-flex gap-3 align-items-center">
                                <i
                                  onClick={() => handleView(user.customerId)}
                                  className="fa-solid fa-eye action-icon"
                                  title="View"
                                ></i>
                                <i
                                  onClick={() => handleEdit(user.customerId)}
                                  className="fa-regular fa-pen-to-square action-icon"
                                  title="Edit"
                                ></i>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {totalCustomers.error ? (
                      <div>
                        <p className="text-danger text-center m-3">
                          {totalCustomers.error}
                        </p>
                      </div>
                    ) : null}

                    {!apiStatus.loading &&
                      !totalCustomers.customers.length > 0 && (
                        <p className="text-center m-3">
                          No Customers data found
                        </p>
                      )}
                      </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </Row>
        </Container>
      </div>
    
  );
}

export default Customers;